import classNames from 'classnames';
import type { PropsWithChildren, ReactNode, RefObject } from 'react';

import { Text } from '@revelio/ui';

import type { PrimaryFilterId } from '../../filters/types/filters';
import type { DataProviderSet } from '../DataProvider/types';
import {
  isDynamicDivergingBarComboPlot,
  type PositionedItem,
  type PositionedItemInfo,
} from '../PageConfigProvider/types/item';
import {
  type MetricValues,
  metricOptions,
} from '../PageConfigProvider/types/metric';
import styles from './ChartHeader.module.css';
import {
  ChartHeaderBreadcrumbBoundary,
  ChartHeaderBreadcrumbTrail,
  useChartHeaderBreadcrumb,
} from './ChartHeaderBreadcrumb';
import { ChartNotSupported } from './ChartNotSupported';
import { useChart } from './ChartProvider';
import { ChartSelector } from './ChartRoot/ChartSelector';
import { isSpecialChartType } from './ChartRoot/classify-chart-data-route';
import { SpecialChartSelector } from './ChartRoot/SpecialChart';
import layoutStyles from './chart-header.module.css';
import { ChartActionMenuWrapper } from './chart-menu/ChartActionMenuWrapper';
import { MetricSwitcher } from './chart-switcher/MetricSwitcher';
import { MultiPrimaryEntitySwitcher } from './chart-switcher/MultiPrimaryEntitySwitcher';
import { PrimaryEntitySwitcher } from './chart-switcher/PrimaryEntitySwitcher';
import { DivergingBarComboControls } from './diverging-bar/DivergingBarComboControls';
import { PlotInfoComp } from './PlotInfoComp';

/** ================================
 * Chart Header
 ================================ */
type ChartHeaderProps = PropsWithChildren<{
  className?: string;
  hasActionMenu?: boolean;
  isCentered?: boolean;
  variant?: 'table' | 'discovery-table' | 'report-table';
}>;

export const ChartHeader = ({
  children,
  className,
  hasActionMenu,
  isCentered,
  variant,
}: ChartHeaderProps) => {
  return (
    <div
      className={classNames(styles.header, className)}
      role="heading"
      aria-level={2}
      data-info-hover
      data-action-menu={hasActionMenu || undefined}
      data-centered={isCentered || undefined}
      data-header-variant={variant}
    >
      {children}
    </div>
  );
};

/** ================================
 * Chart Header Title
 ================================ */
export const ChartHeaderTitle = ({ children }: PropsWithChildren) => {
  return <div className={layoutStyles.headerTitle}>{children}</div>;
};

type ChartHeaderTitleInfoWrapperProps = PropsWithChildren<{
  chartTitleInfo?: ReactNode;
  className?: string;
}>;

export const ChartHeaderTitleInfoWrapper = ({
  chartTitleInfo,
  children,
  className,
}: ChartHeaderTitleInfoWrapperProps) => {
  return (
    <div className={classNames(layoutStyles.titleInfoWrapper, className)}>
      {children}
      {chartTitleInfo}
    </div>
  );
};

export const ChartTitleInfo = ({ info }: { info: PositionedItemInfo }) => {
  const { text, hyperlinks } = info;
  if (hyperlinks && hyperlinks.length > 0) {
    const parts = text.split('<a>');
    return (
      <PlotInfoComp
        plotInfoConfig={{
          popoverPlacement: 'right',
          iconBoxSize: 2.5,
        }}
      >
        <Text tone="inherit" weight="inherit" fontSize="inherit">
          {parts.map((part, idx) => (
            <span key={idx}>
              {part}
              {idx < hyperlinks.length && (
                <Text
                  as="span"
                  weight="inherit"
                  fontSize="inherit"
                  className={styles.infoLink}
                >
                  <a
                    href={hyperlinks?.[idx]?.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {hyperlinks?.[idx]?.text}
                  </a>
                </Text>
              )}
            </span>
          ))}
        </Text>
      </PlotInfoComp>
    );
  }

  return (
    <PlotInfoComp
      plotInfoConfig={{
        popoverPlacement: 'right',
        iconBoxSize: 2.5,
      }}
    >
      <Text
        tone="inherit"
        weight="inherit"
        fontSize="inherit"
        className={styles.inline}
      >
        {text}
      </Text>
    </PlotInfoComp>
  );
};

export const ChartHeaderTitleInfo = ({
  title,
  info,
}: {
  title: string;
  info?: PositionedItemInfo;
}) => {
  const breadcrumb = useChartHeaderBreadcrumb();
  if (breadcrumb !== null && breadcrumb.path.length > 0) {
    return (
      <ChartHeaderBreadcrumbTrail rootTitle={title} breadcrumb={breadcrumb} />
    );
  }
  return (
    <ChartHeaderTitleInfoWrapper
      {...(info && {
        chartTitleInfo: <ChartTitleInfo info={info} />,
      })}
    >
      <ChartHeaderTitle>
        <Text className={styles.title} fontSize="sm" data-chart-title>
          {title}
        </Text>
      </ChartHeaderTitle>
    </ChartHeaderTitleInfoWrapper>
  );
};

/** ================================
 * Chart Action Menu
 ================================ */
export const ChartHeaderAction = ({ children }: PropsWithChildren) => {
  return <div className={layoutStyles.headerAction}>{children}</div>;
};

type ChartActionMenuProps = {
  item: PositionedItem;
  metric?: MetricValues;
  title: string;
  primaryFilterId?: PrimaryFilterId;
  slim: boolean;
  printRef: RefObject<HTMLDivElement>;
  data?: unknown;
  dataType?: string;
  modalContent?: ReactNode;
};

export const ChartActionMenu = ({
  item,
  metric,
  title,
  primaryFilterId,
  slim,
  printRef,
  data,
  dataType,
  modalContent,
}: ChartActionMenuProps) => {
  const { defaultMetricOptions, secondaryDimension, chartType } =
    item?.plot ?? {};

  const metricSwitcherOptions = defaultMetricOptions
    ? metricOptions.filter((option) =>
        defaultMetricOptions.includes(option.value)
      )
    : null;
  const hasSubfilter =
    metric === 'share_of_headcount' || (metricSwitcherOptions?.length ?? 0) > 1;

  const expandedModalContent = (() => {
    if (modalContent) return modalContent;
    if (chartType && isSpecialChartType(chartType)) {
      return <SpecialChartSelector />;
    }
    if (chartType && primaryFilterId && data) {
      return (
        <ChartSelector
          chartType={chartType}
          data={data as DataProviderSet}
          item={item}
          metric={metric}
          secondaryDimension={secondaryDimension}
        />
      );
    }
    return <ChartNotSupported />;
  })();

  return (
    <ChartActionMenuWrapper
      title={((): string => {
        if (hasSubfilter) {
          const metricLabel = metricOptions.find(
            (m) => m.value === metric
          )?.label;
          return metricLabel
            ? `${title || 'Chart'} (${metricLabel})`
            : title || 'Chart';
        }
        return title || 'Chart';
      })()}
      primaryFilterId={primaryFilterId}
      secondaryDimension={secondaryDimension}
      metric={metric}
      slim={slim}
      menu={[
        'expand-plot',
        'share-plot',
        'download-data',
        'download-png',
        'download-ppt',
      ]}
      printRef={printRef}
      chartId={item.id}
      expandModalContent={
        <ChartHeaderBreadcrumbBoundary>
          {expandedModalContent}
        </ChartHeaderBreadcrumbBoundary>
      }
      data={data}
      dataType={dataType}
      plotChartType={chartType}
    />
  );
};

/** ================================
 * Chart Switchers
 ================================ */
type ChartSwitchersProps = {
  primaryFilterId?: PrimaryFilterId;
  isSingleEntityChart: boolean;
  showMultiPrimaryEntitySwitcher?: boolean;
  hasActionMenu?: boolean;
};

export const ChartSwitchers = ({
  primaryFilterId,
  isSingleEntityChart,
  showMultiPrimaryEntitySwitcher,
  hasActionMenu,
}: ChartSwitchersProps) => {
  const { item } = useChart();
  const isDynamicDivergingBarCombo = isDynamicDivergingBarComboPlot(item.plot);
  return (
    <div
      className={layoutStyles.switchers}
      data-action-menu={hasActionMenu || undefined}
    >
      <MetricSwitcher />
      {primaryFilterId && !isDynamicDivergingBarCombo && (
        <PrimaryEntitySwitcher
          isSingleEntityChart={isSingleEntityChart}
          primaryFilterId={primaryFilterId}
        />
      )}
      {isDynamicDivergingBarCombo && primaryFilterId && (
        <DivergingBarComboControls
          plotId={item.id}
          primaryFilterId={primaryFilterId}
          subfilterDimension={item.plot?.divergingBarCombo?.subfilterDimension}
          showEntitySelector={true}
        />
      )}
      {showMultiPrimaryEntitySwitcher && primaryFilterId && (
        <MultiPrimaryEntitySwitcher primaryFilterId={primaryFilterId} />
      )}
    </div>
  );
};
